'use client';

import { SETTINGS_EVENTS } from '@repo/analytics';
import { AnimatedThemeToggler } from '@repo/ui';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { trackEvent } from '@/lib/analytics';

type ThemeChoiceType = 'system' | 'dark' | 'light';

const NEXT_THEME: Record<ThemeChoiceType, ThemeChoiceType> = {
  system: 'dark',
  dark: 'light',
  light: 'system',
};

const NEXT_LABEL: Record<ThemeChoiceType, string> = {
  system: 'Switch to dark mode',
  dark: 'Switch to light mode',
  light: 'Switch to system mode',
};

function ThemeToggleIcon({ choice }: { choice: ThemeChoiceType }): React.JSX.Element {
  if (choice === 'system') {
    return <Monitor className="size-5" aria-hidden />;
  }
  if (choice === 'dark') {
    return <Moon className="size-5" aria-hidden />;
  }
  return <Sun className="size-5" aria-hidden />;
}

export function ThemeToggle(): React.JSX.Element {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const current: ThemeChoiceType = mounted ? ((theme ?? 'system') as ThemeChoiceType) : 'system';

  return (
    <AnimatedThemeToggler
      isDark={resolvedTheme === 'dark'}
      ariaLabel={NEXT_LABEL[current]}
      title={NEXT_LABEL[current]}
      className="flex size-12 items-center justify-center rounded-lg text-foreground hover:bg-accent"
      onToggle={() => {
        const next = NEXT_THEME[current];
        setTheme(next);
        trackEvent(SETTINGS_EVENTS.themeChanged, { from: current, to: next });
      }}
    >
      <ThemeToggleIcon choice={current} />
    </AnimatedThemeToggler>
  );
}
